import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { Campaign } from '../../../model/campaign/Campaign';
import { IUser } from '../../../model/user/IUser';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { formatDate } from '../../../utils/Functions';
import { CampaignsService } from '../../campaigns/CampaignsService';
import { UserService } from '../../user/UserService';
import { NavLink } from 'react-router-dom';
import { debounce } from 'lodash';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { InvoiceService } from '../InvoicesService';
import { IInvoice } from '../../../model/invoice/Invoices';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { ErrorToast } from '../../../utils/toasters';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';

const VERIFY_LIST_COLUMNS: ITableColumn[] = [
    { label: 'Invoice ID' },
    { label: 'Campaign ID' },
    { label: 'Influencer' },
    { label: 'Upload date' },
    { label: 'is Verified?' },
    { label: 'is Rejected?' },
    { label: 'Rejection reason' },
    { label: 'Action' },
];

export default function InvoiceVerifyList() {
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        totalPages: 1,
        isVerified: false,
        isRejected: true,
    });
    const [reportList, setReportList] = useState<IInvoice[]>([]);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);
    const [dataLoading, setDataLoading] = useState(true);

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignId, setCampaignId] = useState('');

    const [users, setUsers] = useState<IUser[]>([]);
    const [userId, setUserId] = useState('');
    const [campaignsParams, setCampaignsParams] = useState('');
    const [usersParams, setUsersParams] = useState('');

    const getCampaigns = (title: string) => {
        CampaignsService.getCampaigns({ perPage: 500, title: title, isLongTerm: false })
            .then(data => {
                setCampaigns(data.data);
            })
            .catch(err => ErrorToast(err));
    };

    const getUsers = (displayName: string) => {
        UserService.getUsers({ perPage: 500, userType: 'influencer', displayName: displayName })
            .then(data => {
                setUsers(data.data);
            })
            .catch(err => ErrorToast(err));
    };

    const campaignsDebounce = useMemo(
        () =>
            debounce((value: string) => {
                getCampaigns(value);
            }, 300),
        []
    );

    const usersDebounce = useMemo(
        () =>
            debounce((value: string) => {
                getUsers(value);
            }, 300),
        []
    );

    useEffect(() => {
        getCampaigns(campaignsParams);
        getUsers(usersParams);

        return () => {
            campaignsDebounce.cancel();
            usersDebounce.cancel();
        };
    }, []);

    useEffect(() => {
        let isCurrent = true;
        setDataLoading(true);

        InvoiceService.getInvoices({ ...pagination, campaignId: campaignId, userId: userId })
            .then(data => {
                if (!isCurrent) return;
                setReportList(data.data);
                setPagination((prev: any) => ({ ...prev, totalPages: Math.ceil(data.count / pagination.perPage) }));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setDataLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [pagination.page, triggerSearch, userId, campaignId]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
    };

    function handleChange(evt: any) {
        const value = evt.target.value;
        setPagination({
            ...pagination,
            [evt.target.name]: value,
        });
    }

    function handleSearch() {
        setPagination(prev => ({ ...prev, page: 1 }));
        setTriggerSearch(prev => !prev);
    }

    return (
        <div>
            <Card
                className="mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleSearch();
                }}
            >
                <Row style={{ rowGap: '18px' }}>
                    <Form.Group
                        as={Col}
                        className="col-4 mb-0 autocomplete-input autocomplete-input zindex-5 position-relative"
                    >
                        <AutocompleteInput
                            items={campaigns}
                            placeholder={'Campaign title'}
                            value={campaignsParams ?? ''}
                            renderItems={['title']}
                            setFilter={value => {
                                campaignsDebounce(value);
                                setCampaignsParams(value);
                                if (!value) {
                                    setCampaignId('');
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }
                            }}
                            onSelect={campaign => {
                                setCampaignId(campaign?.id);
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                        />
                    </Form.Group>
                    <Form.Group
                        as={Col}
                        className="mb-0 autocomplete-input autocomplete-input zindex-5 position-relative"
                    >
                        <AutocompleteInput
                            items={users}
                            placeholder={'Influencer name'}
                            value={usersParams ?? ''}
                            renderItems={['displayName']}
                            setFilter={value => {
                                usersDebounce(value);
                                setUsersParams(value);
                                if (!value) {
                                    setUserId('');
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }
                            }}
                            onSelect={user => {
                                setUserId(user?.id);
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                        />
                    </Form.Group>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Verified?"
                            options={[
                                { value: 'true', label: 'Verified' },
                                { value: 'false', label: 'Not Verified' },
                            ]}
                            selected={toSelectValue((pagination as any).isVerified, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isVerified', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Rejected?"
                            options={[
                                { value: 'true', label: 'Rejected' },
                                { value: 'false', label: 'Not Rejected' },
                            ]}
                            selected={toSelectValue((pagination as any).isRejected, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isRejected', value: value || 'undefined' } })
                            }
                        />
                    </div>
                </Row>
                <div>
                    <Button className="mt-4" type="submit" onClick={handleSearch}>
                        Search
                    </Button>
                </div>
            </Card>
            <TableLayout
                pagination={pagination}
                handlePages={handlePages}
                listLength={reportList.length}
                isLoading={dataLoading}
                dataName="invoices"
                skeletonColumns={VERIFY_LIST_COLUMNS}
                skeletonRows={pagination.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {VERIFY_LIST_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {reportList.map(item => (
                            <tr key={item.id}>
                                <td>{item?.id}</td>
                                <td>
                                    {item?.campaign?.id}
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="campaign-tooltip">{item?.campaign?.title}</Tooltip>}
                                    >
                                        <span className="ml-1">
                                            <FiInfo size={14} className="cursor-pointer" />
                                        </span>
                                    </OverlayTrigger>
                                </td>
                                <td>
                                    ({item?.user?.id}) {item?.user?.displayName}
                                </td>
                                <td>{formatDate(item?.date)}</td>
                                <td>
                                    {item?.isVerified ? (
                                        <span className="word-break text-primary">
                                            <FaCheck />
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {item?.isRejected ? (
                                        <span className="word-break text-primary">
                                            <FaCheck />
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {item?.rejectReason ? (
                                        <textarea
                                            className="form-control"
                                            style={{ minHeight: 'min-content' }}
                                            readOnly
                                            value={item?.rejectReason}
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>
                                    <NavLink className="btn btn-primary" to={`/invoices/verify/edit/${item.id}`}>
                                        Edit
                                    </NavLink>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </div>
    );
}
